import type {
  ReservationInfo,
  TravelerFields,
  TravelerFormValues,
  DepartmentOption
} from '../../../api/reservationInfo';
import type { ShinkansenFormValues } from '../../../types/reservationInfoForm';
import { SHINKANSEN_ADDRESS_TYPES } from '../../../types/reservationInfoForm';

export const buildInitialTravelerValues = (travelerFields: TravelerFields | null): TravelerFormValues | null => {
  if (travelerFields == null) return null;
  const { currentValues } = travelerFields;
  const foreignFlightTraveler = travelerFields.required.includes('flight_first_name_roman');
  return {
    flightFirstName: foreignFlightTraveler
      ? currentValues.flightFirstNameRoman || currentValues.flightFirstName || ''
      : currentValues.flightFirstName || '',
    flightLastName: foreignFlightTraveler
      ? currentValues.flightLastNameRoman || currentValues.flightLastName || ''
      : currentValues.flightLastName || '',
    flightFirstNameKana: currentValues.flightFirstNameKana || '',
    flightLastNameKana: currentValues.flightLastNameKana || '',
    flightFirstNameRoman: currentValues.flightFirstNameRoman || '',
    flightLastNameRoman: currentValues.flightLastNameRoman || '',
    flightBirthday: currentValues.flightBirthday || '',
    flightGender: currentValues.flightGender || '',
    flightTel: currentValues.flightTel || '',
    airSeat: currentValues.airSeat || '',
    mileageNumber: currentValues.mileageNumber || '',
    passportNumber: currentValues.passportNumber || '',
    passportExpire: currentValues.passportExpire || '',
    passportIssuedCountry: currentValues.passportIssuedCountry || ''
  };
};

const parseShinkansenSeat = (
  seat: string | null
): { seat: ShinkansenFormValues['shinkansenSeat']; detail: string } => {
  if (seat == null || seat === '') return { seat: '指定なし', detail: '' };
  const [base, detail] = seat.split(':');
  return { seat: (base as ShinkansenFormValues['shinkansenSeat']) || '指定なし', detail: detail || '' };
};

const defaultPreferredTicketingMethod = (
  current: 'qr' | 'postal' | null,
  settings: NonNullable<ReservationInfo['shinkansenInfo']>['settings']
): 'qr' | 'postal' | '' => {
  if (current) return current;
  if (settings.usePostal && !settings.useQr) return 'postal';
  if (settings.useQr) return 'qr';
  return '';
};

export const buildInitialShinkansenValues = (
  shinkansenInfo: ReservationInfo['shinkansenInfo'],
  departments: DepartmentOption[]
): ShinkansenFormValues | null => {
  if (shinkansenInfo == null) return null;
  const { currentValues, settings } = shinkansenInfo;
  const seat = parseShinkansenSeat(currentValues.shinkansenSeat);
  const addressType =
    currentValues.shinkansenAddressType ||
    (settings.enabledCustomizeAddress
      ? SHINKANSEN_ADDRESS_TYPES.text
      : settings.hasDepartments
      ? SHINKANSEN_ADDRESS_TYPES.select
      : SHINKANSEN_ADDRESS_TYPES.organizationBaseSelect);
  const defaultDepartmentId = settings.hasDepartments ? departments[0]?.id || null : null;
  const defaultBaseId = null;
  const selectedDepartment = departments.find(dept => dept.id === defaultDepartmentId);
  const addressFromDepartment = selectedDepartment?.address || '';
  const addresseeFromDepartment = selectedDepartment?.addressee || '';
  const postcodeFromDepartment = selectedDepartment?.postalCode || '';
  const addressFromBase = '';
  const addresseeFromBase = '';
  const postcodeFromBase = '';
  const organizationAddressId = selectedDepartment?.organizationAddressId || null;

  return {
    shinkansenAddressType: addressType,
    postcode:
      addressType === SHINKANSEN_ADDRESS_TYPES.select
        ? currentValues.postcode || postcodeFromDepartment
        : addressType === SHINKANSEN_ADDRESS_TYPES.organizationBaseSelect
        ? postcodeFromBase
        : currentValues.postcode || '',
    shinkansenAddress:
      addressType === SHINKANSEN_ADDRESS_TYPES.select
        ? currentValues.shinkansenAddress || addressFromDepartment
        : addressType === SHINKANSEN_ADDRESS_TYPES.organizationBaseSelect
        ? addressFromBase
        : currentValues.shinkansenAddress || '',
    shinkansenAddressee:
      addressType === SHINKANSEN_ADDRESS_TYPES.select
        ? currentValues.shinkansenAddressee || addresseeFromDepartment
        : addressType === SHINKANSEN_ADDRESS_TYPES.organizationBaseSelect
        ? addresseeFromBase
        : currentValues.shinkansenAddressee || '',
    shinkansenTicketType: currentValues.shinkansenTicketType || 'normal',
    shinkansenSeatType: currentValues.shinkansenSeatType || 'reserve',
    shinkansenSeat: seat.seat,
    shinkansenSeatDetail: seat.detail,
    exicNumber: currentValues.exicNumber || '',
    exicPassword: '',
    exicCardId: currentValues.exicCardId || null,
    departmentId: defaultDepartmentId,
    organizationBaseId: defaultBaseId,
    organizationAddressId: addressType === SHINKANSEN_ADDRESS_TYPES.select ? organizationAddressId : null,
    preferredTicketingMethod: defaultPreferredTicketingMethod(currentValues.preferredTicketingMethod, settings)
  };
};
